import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import store from 'store'

import { activate } from '../../../routing/fetchingfunction/user/user.action'

export const ConfirmEmailRedirect = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { uid, token } = useParams()
  const [searchParams] = useSearchParams()

  const [isProcessing, setIsProcessing] = useState(true) // Combined state for loading/processing
  const [activationError, setActivationError] = useState(null)
  const [alreadyActivated, setAlreadyActivated] = useState(false)
  const [activationAttempted, setActivationAttempted] = useState(false) // New state

  const userRoleFromStore = store.get('user_role') || 'student' // Default to student

  useEffect(() => {
    let isMounted = true // To prevent state updates on unmounted component

    const processActivation = async () => {
      if (activationAttempted && isMounted) {
        // Check if already attempted
        console.log('ConfirmEmailRedirect: Activation already attempted, skipping.')
        // If already attempted, it might be processing or might have failed.
        // If it failed, error state would be set. If processing, isProcessing is true.
        // If it succeeded and navigated, this instance of the component should ideally be unmounted.
        // We might not need to explicitly setIsProcessing(false) here if navigation occurs.
        return
      }

      // Mark as attempted immediately
      // Note: if component unmounts and remounts for the same URL, this flag resets.
      // This is okay as a new instance would mean a new attempt is warranted if previous failed to navigate.
      if (isMounted) setActivationAttempted(true)

      setIsProcessing(true) // Ensure processing state is true at the start
      setActivationError(null)
      setAlreadyActivated(false)

      let urlUid, urlToken
      const pathParts = location.pathname.split('/')
      if (pathParts.length >= 2) {
        urlUid = pathParts[pathParts.length - 2]
        urlToken = pathParts[pathParts.length - 1]
      }
      if ((!urlUid || !urlToken) && uid && token) {
        urlUid = uid
        urlToken = token
      }
      if ((!urlUid || !urlToken) && searchParams) {
        urlUid = searchParams.get('uid64') || searchParams.get('uid')
        urlToken = searchParams.get('token')
      }

      if (!urlUid || !urlToken) {
        if (isMounted) {
          setActivationError('Invalid activation link. Please check your email and try again.')
          setIsProcessing(false)
        }
        return
      }

      store.remove('access_token')
      store.remove('refresh_token')
      store.remove('user_data')

      try {
        console.log(
          `ConfirmEmailRedirect: Dispatching activate with uid: ${urlUid}, token: ${urlToken}`
        )
        await dispatch(activate({ uid64: urlUid, token: urlToken })).unwrap()
        console.log('ConfirmEmailRedirect: Account activated successfully. Navigating...')
        if (isMounted) {
          // Determine role for navigation. Prefer role from store if activation doesn't set it early enough for this navigate call.
          const roleToNavigate = store.get('user_role') || userRoleFromStore
          navigate(`/${roleToNavigate}/register/profile-update`)
          // No need to set isProcessing to false here as we are navigating away
        }
      } catch (error) {
        console.error('ConfirmEmailRedirect: Activation failed:', error)
        if (isMounted) {
          if (
            error?.message?.includes('already active') ||
            error?.data?.detail?.includes('already active')
          ) {
            setAlreadyActivated(true)
            // If already active, also navigate to registration page to resume flow
            const roleToNavigate = store.get('user_role') || userRoleFromStore
            console.log(
              'ConfirmEmailRedirect: Account already active. Navigating to registration to resume.'
            )
            navigate(`/${roleToNavigate}/register/profile-update`)
            return // Skip further error state setting as we are navigating
          } else {
            setActivationError(
              error.message || error?.data?.detail || 'Activation failed. Please try again.'
            )
          }
          setIsProcessing(false)
        }
      }
    }

    processActivation()

    return () => {
      isMounted = false
    }
    // Added userRoleFromStore to dependencies, ensure it captures role correctly if needed for navigation logic
  }, [dispatch, location, navigate, uid, token, searchParams, userRoleFromStore])

  // UI for loading or error states
  if (isProcessing) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-16 px-4 bg-[#FEEBDD] rounded-lg max-w-[800px] mx-auto my-16 text-[#F67443]">
        <h1 className="text-4xl font-semibold mb-6 text-center">Processing Activation...</h1>
        <div className="w-16 h-16 border-4 border-[#F67443] border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    )
  }

  if (activationError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-16 px-4 bg-[#FEEBDD] rounded-lg max-w-[800px] mx-auto my-16 text-[#F67443]">
        <div className="bg-red-100 text-red-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold mb-2 text-red-600">Activation Failed</h2>
        <p className="text-lg mb-6">{activationError}</p>
        <button
          onClick={() => {
            const roleToNavigate = store.get('user_role') || userRoleFromStore
            // On error, reset local step to 1 to ensure clean start of registration if they click this
            store.set('register_step', 1)
            store.set('switch_step', 1)
            navigate(`/${roleToNavigate}/register`)
          }}
          className="px-6 py-2 bg-[#F67443] text-white rounded-md hover:bg-opacity-90 transition-colors"
        >
          Go to Registration
        </button>
      </div>
    )
  }

  // Fallback: Should ideally not be reached if logic is correct and navigation happens on success/already_active
  return (
    <div className="flex-1 flex flex-col items-center justify-center py-16 px-4 bg-[#FEEBDD] rounded-lg max-w-[800px] mx-auto my-16 text-[#F67443]">
      <h1 className="text-4xl font-semibold mb-6 text-center">Activation Status</h1>
      <p className="text-lg">Please wait or check your registration status.</p>
      <button
        onClick={() => {
          const roleToNavigate = store.get('user_role') || userRoleFromStore
          navigate(`/${roleToNavigate}/register`)
        }}
        className="mt-4 px-6 py-2 bg-[#F67443] text-white rounded-md hover:bg-opacity-90 transition-colors"
      >
        Check Registration Status
      </button>
    </div>
  )

  // Removed the old UI rendering logic for countdown etc.
}

export default ConfirmEmailRedirect
